// engine2025/params/scales.ts — F10 (§A.11): 2025 tax scales (arts. 63, 65, 66, 74, 76 LIRPF).
// Source: docs/guia-renta-2025/15-cuota-integra-estatal-autonomica.md (Cap.15, Manual
// pp. 1116-1150). The four scales flagged ⚠️VERIFICAR in the chapter (Asturias, Canarias,
// Cantabria, La Rioja) are resolved as VERIFICADO in docs/guia-renta-2025/
// parametros-verificados.md (checked against BOE/AEAT); values below follow that resolution.

import { Cents, parseEuros } from '../money';
import { CCAA } from '../types';
import { ParamRegistry } from './registry';

/** One progressive-scale bracket: marginal rate up to `upTo` (null = last bracket). */
export interface ScaleBracket {
    upTo: Cents | null;
    ratePct: number;
}

const e = parseEuros;
const b = (upToEuros: string | null, ratePct: number): ScaleBracket => ({
    upTo: upToEuros === null ? null : e(upToEuros),
    ratePct,
});

/** Registers every Cap.15 scale and quota param. */
export function registerScaleParams(params: ParamRegistry): void {
    const src = (page: string) => ({ chapter: 'Cap.15 — 15-cuota-integra-estatal-autonomica.md', page });
    const srcV = (page: string) => ({
        chapter: 'Cap.15 + parametros-verificados.md (verificación BOE/AEAT)',
        page,
    });

    // 4.1 — Escala general estatal (art. 63.1.1º).
    params.define(
        'P-150',
        [b('12.450,00', 9.5), b('20.200,00', 12), b('35.200,00', 15), b('60.000,00', 18.5), b('300.000,00', 22.5), b(null, 24.5)],
        { concept: 'Escala general del IRPF — tramo estatal (art. 63.1.1º)', verified: true, source: src('p. 1119-1120') }
    );

    // 4.2 — Escala del art. 65 (Ceuta y Melilla; complementaria de residentes en el extranjero).
    params.define(
        'P-NEW-18-1',
        [b('12.450,00', 9.5), b('20.200,00', 12), b('35.200,00', 15), b('60.000,00', 18.5), b(null, 22.5)],
        { concept: 'Escala del art. 65 LIRPF (Ceuta/Melilla y residentes en el extranjero)', verified: true, source: src('p. 1129-1130, 1133-1134') }
    );

    // 4.3 — Escalas autonómicas generales 2025 (P-151-XX).
    const regional: Record<string, { brackets: ScaleBracket[]; page: string; verified?: 'ext' }> = {
        AN: { brackets: [b('13.000,00', 9.5), b('21.100,00', 12), b('35.200,00', 15), b('60.000,00', 18.5), b(null, 22.5)], page: 'p. 1120-1121' },
        AR: { brackets: [b('13.072,50', 9.5), b('21.210,00', 12), b('36.960,00', 15), b('52.500,00', 18.5), b('60.000,00', 20.5), b('80.000,00', 23), b('90.000,00', 24), b('130.000,00', 25), b(null, 25.5)], page: 'p. 1121-1122' },
        AS: { brackets: [b('12.450,00', 9), b('17.707,20', 12), b('33.007,20', 14), b('53.407,20', 19.2), b('70.000,00', 21.5), b('90.000,00', 22.5), b('175.000,00', 25), b(null, 26)], page: 'p. 1121-1123', verified: 'ext' },
        IB: { brackets: [b('10.000,00', 9), b('18.000,00', 11.25), b('30.000,00', 14.25), b('48.000,00', 17.5), b('70.000,00', 19), b('90.000,00', 21.75), b('120.000,00', 22.75), b('175.000,00', 23.75), b(null, 24.75)], page: 'p. 1123' },
        CN: { brackets: [b('13.748,00', 9), b('19.422,00', 11.5), b('35.924,00', 14), b('57.566,00', 18.5), b('93.268,00', 23.5), b('123.745,00', 25), b(null, 26)], page: 'p. 1123-1124', verified: 'ext' },
        CB: { brackets: [b('13.000,00', 8.5), b('21.000,00', 11), b('35.200,00', 14.5), b('60.000,00', 18), b('90.000,00', 22.5), b(null, 24.5)], page: 'p. 1124', verified: 'ext' },
        CM: { brackets: [b('12.450,00', 9.5), b('20.200,00', 12), b('35.200,00', 15), b('60.000,00', 18.5), b(null, 22.5)], page: 'p. 1124-1125' },
        CL: { brackets: [b('12.450,00', 9), b('20.200,00', 12), b('35.200,00', 14), b('53.407,20', 18.5), b(null, 21.5)], page: 'p. 1125-1126' },
        CT: { brackets: [b('12.500,00', 9.5), b('22.000,00', 12.5), b('33.000,00', 16), b('53.000,00', 19), b('90.000,00', 21.5), b('120.000,00', 23.5), b('175.000,00', 24.5), b(null, 25.5)], page: 'p. 1126' },
        EX: { brackets: [b('12.450,00', 8), b('20.200,00', 10), b('24.200,00', 16), b('35.200,00', 17.5), b('60.000,00', 21), b('80.200,00', 23.5), b('99.200,00', 24), b('120.200,00', 24.5), b(null, 25)], page: 'p. 1126-1127' },
        GA: { brackets: [b('12.985,35', 9), b('21.068,60', 11.65), b('35.200,00', 14.9), b('60.000,00', 18.4), b(null, 22.5)], page: 'p. 1127' },
        MD: { brackets: [b('13.362,22', 8.5), b('19.004,63', 10.7), b('35.425,68', 12.8), b('57.320,40', 17.4), b(null, 20.5)], page: 'p. 1127-1128' },
        MC: { brackets: [b('12.450,00', 9.5), b('20.200,00', 11.2), b('34.000,00', 13.3), b('60.000,00', 17.9), b(null, 22.5)], page: 'p. 1128' },
        RI: { brackets: [b('12.450,00', 8), b('20.200,00', 10.6), b('35.200,00', 13.6), b('40.000,00', 17.8), b('50.000,00', 18.3), b('60.000,00', 19), b('120.000,00', 24.5), b(null, 27)], page: 'p. 1128-1129', verified: 'ext' },
        VC: { brackets: [b('12.000,00', 9), b('22.000,00', 12), b('32.000,00', 15), b('42.000,00', 17.5), b('52.000,00', 20), b('62.000,00', 22.5), b('72.000,00', 25), b('100.000,00', 26.5), b('150.000,00', 27.5), b('200.000,00', 28.5), b(null, 29.5)], page: 'p. 1128-1129' },
    };
    for (const [ccaa, def] of Object.entries(regional)) {
        params.define(`P-151-${ccaa}`, def.brackets, {
            concept: `Escala autonómica general 2025 — ${ccaa}`,
            verified: true,
            source: def.verified === 'ext' ? srcV(def.page) : src(def.page),
        });
    }

    // 4.4 / 4.5 — Escalas del ahorro estatal (art. 66.1) y autonómica (art. 76), idénticas.
    const savings = [b('6.000,00', 9.5), b('50.000,00', 10.5), b('200.000,00', 11.5), b('300.000,00', 13.5), b(null, 15)];
    params.define('P-152', savings, {
        concept: 'Escala del ahorro — tramo estatal (art. 66.1)',
        verified: true,
        source: src('p. 1130-1131'),
    });
    params.define('P-153', savings, {
        concept: 'Escala del ahorro — tramo autonómico (art. 76, común a todas las CCAA; idéntica a P-152)',
        verified: true,
        source: src('p. 1130-1131'),
    });

    // 4.6 — Escala del ahorro para residentes en el extranjero (art. 66.2, ya duplicada).
    params.define(
        'P-NEW-18-2',
        [b('6.000,00', 19), b('50.000,00', 21), b('200.000,00', 23), b('300.000,00', 27), b(null, 30)],
        { concept: 'Escala del ahorro residentes en el extranjero (art. 66.2, estatal+autonómica unificada)', verified: true, source: src('p. 1135') }
    );

    // 4.8 — Incremento del mínimo por anualidades por alimentos (arts. 64/75).
    params.define('P-154', parseEuros('1.980,00'), {
        concept: 'Incremento del mínimo del contribuyente cuando se satisfacen anualidades por alimentos a hijos',
        verified: true,
        source: src('p. 1147'),
    });
}

/** General scale of the CCAA of residence (Ceuta/Melilla use the art. 65 scale, DA 32ª). */
export function getRegionalGeneralScale(ccaa: CCAA, params: ParamRegistry): ScaleBracket[] {
    if (ccaa === 'OUT_OF_SCOPE') {
        throw new Error('Escala autonómica: País Vasco/Navarra (forales) fuera de alcance del motor');
    }
    if (ccaa === 'CE' || ccaa === 'ML') return params.get<ScaleBracket[]>('P-NEW-18-1');
    return params.get<ScaleBracket[]>(`P-151-${ccaa}`);
}
